import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { EligibleListDTO, EligibleRowDTO, EventDetailDTO, PreviewDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { Chip, dinero, errorText, fechaEvento, normalizar } from "./comun";
import { QvpCuerpo } from "./queVaAPasar";

interface Props { campusId: number; evento: EventDetailDTO; onClose: () => void; onDone: (t: string, ok: boolean) => void; }

type Filtro = "todas" | "listas" | "reponer" | "online";
const TOPE = 300;

/**
 * «+ Agregar Graduadas» (antes la pestaña Elegibles, Módulos Claros 2026-11-01): horario que termina cerca del evento, Diploma Listo o
 * N Por Reponer; se eligen y se agregan en lote con la confirmación de siempre (la que se puso tras el error del 28-oct en TGU).
 */
const AgregarGraduadas: React.FC<Props> = ({ campusId, evento, onClose, onDone }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [data, setData] = useState<EligibleListDTO | null>(null);
	const [filtro, setFiltro] = useState<Filtro>("todas");
	const [curso, setCurso] = useState<number>(0);
	const [q, setQ] = useState<string>("");
	const [sel, setSel] = useState<number[]>([]);
	const [cobra, setCobra] = useState<boolean>((evento.packagePrice || 0) > 0);
	const [busy, setBusy] = useState<boolean>(false);
	const [tick, setTick] = useState<number>(0);
	const [confirmar, setConfirmar] = useState<boolean>(false);
	const [pv, setPv] = useState<PreviewDTO | null>(null);
	const [pvError, setPvError] = useState<string | null>(null);
	const peticion = useRef<number>(0); // only the answer to the latest «Qué Va a Pasar» is painted

	useEffect(() => {
		let vigente = true;
		setData(null); setSel([]);
		http.eventsService.eligible(campusId, evento.id)
			.then(r => vigente && setData(r.data))
			.catch(e => { if (vigente) { setData({ eventId: evento.id, rows: [], counts: {} }); onDone(errorText(e), false); } });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, evento.id, tick]);

	const cursos = useMemo(() => {
		const mapa = new Map<number, string>();
		(data?.rows || []).forEach(r => { if (r.courseId) mapa.set(r.courseId, cursoCorto(r.courseName) || r.courseName); });
		return Array.from(mapa.entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
	}, [data]);
	const filas = useMemo(() => {
		const t = normalizar(q.trim());
		return (data?.rows || []).filter(r => (filtro === "todas" || (filtro === "online" ? !!r.online : filtro === "listas" ? r.diplomaReady : !r.diplomaReady))
			&& (!curso || r.courseId === curso)
			&& (!t || normalizar([r.studentName, r.identityCode, r.courseName].join(" ")).includes(t)));
	}, [data, filtro, q, curso]);
	const visibles = filas.slice(0, TOPE);
	const bloqueada = (r: EligibleRowDTO) => r.alreadyAdded || !!r.goingTo;
	const libres = visibles.filter(r => !bloqueada(r)).map(r => r.enrollmentId);
	const todas = libres.length > 0 && libres.every(id => sel.includes(id));
	const elegidas = (data?.rows || []).filter(r => sel.includes(r.enrollmentId));
	const precio = evento.packagePrice || 0;
	const m = (v: number) => dinero(evento.currency, v);
	const alternar = (id: number) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

	// La confirmación sale del servidor (mismo recorrido que el lote real): quién entra, quién no y el total que de verdad se carga
	const pedirConfirmacion = () => {
		const n = ++peticion.current;
		setConfirmar(true); setPv(null); setPvError(null);
		http.eventsService.previewAdd(campusId, { eventId: evento.id, action: "attendees", lenient: true, items: sel.map(eid => ({ kind: "graduada" as const, enrollmentId: eid, charge: cobra })) })
			.then(r => { if (n === peticion.current) setPv(r.data); })
			.catch(e => { if (n === peticion.current) setPvError(errorText(e)); });
	};
	const salen = pv ? (pv.skipped || []).length : 0;
	const entran = pv ? Math.max(0, sel.length - salen) : 0;
	const agregar = () => {
		setBusy(true);
		http.eventsService.addEligible(campusId, evento.id, sel, cobra)
			.then(r => {
				const skipped = r.data.skipped || [];
				setConfirmar(false);
				onDone(S.agregadas(r.data.added, skipped.length) + (skipped.length ? " " + skipped.slice(0, 3).map((x: { reason: string }) => x.reason).join(" ") : ""), skipped.length === 0);
				setTick(x => x + 1);
			})
			.catch(e => onDone(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const estado = (r: EligibleRowDTO) => r.alreadyAdded ? <Chip>{S.yaAgregada}</Chip>
		: r.goingTo ? <Chip tono="oro">{S.yaVaA(r.goingTo)}</Chip>
		: r.diplomaReady ? <Chip tono="acento">{S.elegListas}</Chip> : <Chip tono="oro">{S.nPorReponer(r.sessionsToRecover)}</Chip>;
	const chips: { k: Filtro; t: string; n?: number }[] = [
		{ k: "todas", t: S.elegTodas, n: data?.counts?.eligible }, { k: "listas", t: S.elegListas, n: data?.counts?.diplomaReady }, { k: "reponer", t: S.elegReponer, n: data?.counts?.toRecover },
		...(data?.counts?.online ? [{ k: "online" as Filtro, t: S.online, n: data.counts.online }] : [])
	];

	return (
		<Modal className="waModulo wevVentana" show onHide={busy ? undefined : onClose} size="xl" centered scrollable fullscreen="md-down">
			<Modal.Header closeButton={!busy}><Modal.Title style={{ fontSize: 18 }}>{confirmar ? S.confirmarLoteTitulo(sel.length) : S.agregarGraduadasTitulo}</Modal.Title></Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15 }}>
				{confirmar ? (
					<div>
						<div className="wevSub" style={{ marginBottom: 6 }}>{evento.name} · {fechaEvento(evento.dateTime || evento.date)}</div>
						{pv ? <>
							<QvpCuerpo pv={pv} />
							{(pv.skipped || []).length ? <div className="wevQvp" style={{ marginTop: 8 }}><ul>
								{(pv.skipped || []).map((x, i) => <li key={i} className="wevOro">{S.qvpNoEntra(x.name || elegidas.find(r => r.enrollmentId === x.enrollmentId)?.studentName || "", x.reason)}</li>)}
							</ul></div> : null}
						</> : <div className="wevQvp"><h6>{S.qvp}</h6>{pvError ? <div className="error">{pvError}</div> : <div className="wevSub">{S.qvpCalculando}</div>}</div>}
					</div>
				) : <>
					<div className="wau-chips" role="group" style={{ alignItems: "center" }}>
						{chips.map(c => <button key={c.k} type="button" className="wau-chip" aria-pressed={filtro === c.k} onClick={() => setFiltro(c.k)}>{c.t}{c.n != null ? <span className="wau-num"> {c.n}</span> : null}</button>)}
						<select className="wau-entrada" style={{ width: movil ? "100%" : 220, marginLeft: movil ? 0 : "auto" }} aria-label={S.colCurso} value={curso} onChange={e => setCurso(Number(e.target.value) || 0)}>
							<option value={0}>{S.cursoTodos}</option>
							{cursos.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
						</select>
						<input type="search" className="wau-entrada" style={{ width: movil ? "100%" : 220 }} placeholder={S.buscarGraduada} value={q} onChange={e => setQ(e.target.value)} />
					</div>
					{data === null ? <div className="loader" /> : (
						<div className="wevElegibles" style={{ marginTop: 10 }}>
							{filas.length > TOPE ? <div className="wevSub">{S.mostrando(TOPE, filas.length)}</div> : null}
							<table className="table wevLista" style={{ marginBottom: 0 }}>
								<thead><tr>
									<th style={{ width: 30 }}><input type="checkbox" checked={todas} disabled={libres.length === 0} aria-label={S.elegTodas} onChange={() => setSel(todas ? [] : libres)} /></th>
									<th>{S.colNombre}</th>{movil ? null : <><th>{S.colCurso}</th><th>{S.colTermina}</th><th className="wevNum">{S.colAsistencia}</th></>}<th>{S.colEstado}</th>
								</tr></thead>
								<tbody>
									{visibles.length === 0 ? <tr><td colSpan={6} className="wevVacio">{S.sinResultados}</td></tr> : visibles.map(r => (
										<tr key={r.enrollmentId} className={sel.includes(r.enrollmentId) ? "sel" : ""} onClick={() => !bloqueada(r) && alternar(r.enrollmentId)}>
											<td><input type="checkbox" disabled={bloqueada(r)} checked={r.alreadyAdded || sel.includes(r.enrollmentId)} aria-label={r.studentName} onClick={e => e.stopPropagation()} onChange={() => alternar(r.enrollmentId)} /></td>
											<td><b>{r.studentName}</b>{r.online ? <> <Chip>{S.online}</Chip></> : null}<div className="wevSub">{movil ? cursoCorto(r.courseName) || r.courseName : r.identityCode}</div></td>
											{movil ? null : <>
												<td style={{ fontSize: 14 }}>{cursoCorto(r.courseName) || r.courseName}<div className="wevSub">{r.scheduleName}</div></td>
												<td style={{ whiteSpace: "nowrap" }}>{fechaEvento(r.scheduleEndDate, false)}</td>
												<td className={"wevNum" + ((r.attendancePct ?? 0) < r.minAttendancePct ? " wevOro" : "")}>{r.attendancePct != null ? r.attendancePct + " %" : "-"}</td>
											</>}
											<td>{estado(r)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</>}
			</Modal.Body>
			<Modal.Footer className="wevPie">
				{confirmar ? <>
					<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmar(false)} disabled={busy}>{S.volver}</button>
					<button type="button" className="btn btn-primary btn-sm" onClick={agregar} disabled={busy || !pv || !pv.ok || entran === 0}>{busy ? S.guardando : S.confirmarLoteSi(pv ? entran : sel.length)}</button>
				</> : <>
					<label className="wev-check"><input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} /><span>{S.cobraPaquete}</span></label>
					<span style={{ flex: 1 }} />
					<button type="button" className="btn btn-primary btn-sm" disabled={busy || sel.length === 0} onClick={pedirConfirmacion}>{S.agregarN(sel.length)}</button>
				</>}
			</Modal.Footer>
		</Modal>
	);
};

export default AgregarGraduadas;
